import React from 'react';
import InovxLoader from '@shared/global/InovxLoader';
import {
    changeExpiredLocalPassword,
    PlatformSessionLocalAuthError,
} from './platformSession';
import '@styles/login.less';

interface LocalPasswordChangePageProps {
    username: string;
    onPasswordChanged: () => void;
    onCancel: () => void;
}

interface FieldErrors {
    oldPassword?: boolean;
    newPassword?: boolean;
    confirmPassword?: boolean;
}

function getFailureMessage(error: PlatformSessionLocalAuthError): string {
    switch (error.response?.Reason?.toLowerCase()) {
        case 'incorrectusernameorpassword':
            return InovxLoader.t('common:auth.invalidOldPassword');
        case 'passworddoesnotmatch':
            return InovxLoader.t('common:auth.passwordsDoNotMatch');
        case 'passwordruleviolation':
            return error.response?.Message || InovxLoader.t('common:auth.passwordRuleViolation');
        default:
            return error.response?.Message || InovxLoader.t('common:auth.passwordChangeFailed');
    }
}

function mapPropertyName(propertyName?: string): keyof FieldErrors | null {
    switch (propertyName?.toLowerCase()) {
        case 'oldpassword':
            return 'oldPassword';
        case 'newpassword':
            return 'newPassword';
        case 'confirmpassword':
            return 'confirmPassword';
        default:
            return null;
    }
}

const LocalPasswordChangePage: React.FC<LocalPasswordChangePageProps> = ({ username, onPasswordChanged, onCancel }) => {
    const [oldPassword, setOldPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [errorMessage, setErrorMessage] = React.useState('');
    const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({});
    const [submitting, setSubmitting] = React.useState(false);

    const clearPasswordFields = () => {
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage('');
        setFieldErrors({});

        const nextFieldErrors: FieldErrors = {};
        let nextErrorMessage = '';
        if (!oldPassword) {
            nextFieldErrors.oldPassword = true;
        }
        if (!newPassword) {
            nextFieldErrors.newPassword = true;
        }
        if (!confirmPassword) {
            nextFieldErrors.confirmPassword = true;
        }
        if (newPassword && confirmPassword && newPassword !== confirmPassword) {
            nextErrorMessage = InovxLoader.t('common:auth.passwordsDoNotMatch');
            nextFieldErrors.confirmPassword = true;
        }

        if (Object.keys(nextFieldErrors).length > 0) {
            setErrorMessage(nextErrorMessage || InovxLoader.t('common:message.requiredInput'));
            setFieldErrors(nextFieldErrors);
            return;
        }

        setSubmitting(true);
        try {
            await changeExpiredLocalPassword({
                username,
                oldPassword,
                newPassword,
                confirmPassword,
            });
            onPasswordChanged();
        } catch (error) {
            if (error instanceof PlatformSessionLocalAuthError) {
                const message = getFailureMessage(error);
                const fieldName = mapPropertyName(error.response?.PropertyName);
                setErrorMessage(message);
                if (fieldName) {
                    setFieldErrors({ [fieldName]: true });
                }
            } else if (error instanceof Error) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage(InovxLoader.t('common:auth.passwordChangeFailed'));
            }
            clearPasswordFields();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="login-page-container">
            <div className="login-inner">
                <div className="login-panel panel registration-panel">
                    <div className="login-header registration-header">
                        <div className="welcome-text">{InovxLoader.t('common:auth.passwordExpired')}</div>
                        <div className="vlogo registration-logo">
                            <img alt="Visionaize" src="/images/visionaize-login-logo.png" />
                        </div>
                        <div className="registration-for">
                            <div className="registration-username">{username}</div>
                        </div>
                    </div>
                    <form className="form-horizontal registration-form" role="form" onSubmit={handleSubmit}>
                        {errorMessage && (
                            <div className="text-danger registration-error" role="alert">
                                {errorMessage}
                            </div>
                        )}

                        <div className={`input-group has-info${fieldErrors.oldPassword ? ' has-error' : ''}`}>
                            <div className="input-group-addon"><span className="glyphicon glyphicon-lock" title={InovxLoader.t('common:auth.currentPassword')}></span></div>
                            <input
                                className="form-control"
                                type="password"
                                placeholder={InovxLoader.t('common:auth.currentPassword')}
                                title={InovxLoader.t('common:auth.currentPassword')}
                                autoComplete="current-password"
                                value={oldPassword}
                                onChange={(event) => setOldPassword(event.target.value)}
                            />
                        </div>

                        <div className={`input-group has-info${fieldErrors.newPassword ? ' has-error' : ''}`}>
                            <div className="input-group-addon"><span className="glyphicon glyphicon-lock" title={InovxLoader.t('common:auth.newPassword')}></span></div>
                            <input
                                className="form-control"
                                type="password"
                                placeholder={InovxLoader.t('common:auth.newPassword')}
                                title={InovxLoader.t('common:auth.newPassword')}
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(event) => setNewPassword(event.target.value)}
                            />
                        </div>

                        <div className={`input-group has-info${fieldErrors.confirmPassword ? ' has-error' : ''}`}>
                            <div className="input-group-addon"><span className="glyphicon glyphicon-lock" title={InovxLoader.t('common:auth.confirmNewPassword')}></span></div>
                            <input
                                className="form-control"
                                type="password"
                                placeholder={InovxLoader.t('common:auth.confirmNewPassword')}
                                title={InovxLoader.t('common:auth.confirmNewPassword')}
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(event) => setConfirmPassword(event.target.value)}
                            />
                        </div>

                        <button type="submit" className="btn btn-info btn-width-full register-button" disabled={submitting}>
                            {submitting ? InovxLoader.t('common:auth.changingPassword') : InovxLoader.t('common:auth.changePasswordAndSignIn')}
                        </button>
                        <button type="button" className="btn btn-default btn-width-full login-secondary-button" onClick={onCancel} disabled={submitting}>
                            {InovxLoader.t('common:form.cancel')}
                        </button>
                    </form>
                </div>
            </div>
            <p className="inovx-copyright">{InovxLoader.t('common:about.copyright')}</p>
        </div>
    );
};

export default LocalPasswordChangePage;
